import { useDataProvider, useGetIdentity, useNotify } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

import { CALL_SLOTS } from "./gulaConstants";
import { Field, SimpleSelect } from "./SimpleSelect";

const EMPTY = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  postalCode: "",
  city: "",
  callSlot: "",
};

/** Manual lead (phone call, referral, WhatsApp…). */
export const NewLeadDialog = ({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const set = (field: keyof typeof EMPTY) => (value: string) =>
    setForm((previous) => ({ ...previous, [field]: value }));

  const handleCreate = async () => {
    if (!form.firstName || (!form.phone && !form.email)) {
      notify("Pon al menos el nombre y un teléfono o email", {
        type: "warning",
      });
      return;
    }
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const { data: contact } = await dataProvider.create("contacts", {
        data: {
          first_name: form.firstName,
          last_name: form.lastName,
          phone_jsonb: form.phone ? [{ number: form.phone, type: "Work" }] : [],
          email_jsonb: form.email ? [{ email: form.email, type: "Work" }] : [],
          first_seen: now,
          last_seen: now,
          sales_id: identity?.id,
          tags: [],
        },
      });
      // The database adds the contact to the pipeline; fill in the lead details
      const { data: rows } = await dataProvider.getList("gula_clients", {
        filter: { contact_id: contact.id },
        pagination: { page: 1, perPage: 1 },
        sort: { field: "id", order: "ASC" },
      });
      if (rows[0]) {
        await dataProvider.update("gula_clients", {
          id: rows[0].id,
          data: {
            postal_code: form.postalCode || null,
            city: form.city || null,
            call_slot: form.callSlot || null,
            source: "manual",
            // Whoever creates a lead by hand is working it
            sales_id: identity?.id ?? null,
          },
          previousData: rows[0],
        });
      }
      notify("Lead creado", { type: "success" });
      setForm(EMPTY);
      onCreated();
    } catch (error) {
      console.error(error);
      notify("No se ha podido crear el lead", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nuevo lead</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nombre">
            <Input
              value={form.firstName}
              onChange={(event) => set("firstName")(event.target.value)}
            />
          </Field>
          <Field label="Apellidos">
            <Input
              value={form.lastName}
              onChange={(event) => set("lastName")(event.target.value)}
            />
          </Field>
          <Field label="Teléfono">
            <Input
              type="tel"
              value={form.phone}
              onChange={(event) => set("phone")(event.target.value)}
            />
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={form.email}
              onChange={(event) => set("email")(event.target.value)}
            />
          </Field>
          <Field label="Código postal">
            <Input
              value={form.postalCode}
              onChange={(event) => set("postalCode")(event.target.value)}
            />
          </Field>
          <Field label="Población">
            <Input
              value={form.city}
              onChange={(event) => set("city")(event.target.value)}
            />
          </Field>
          <div className="col-span-2">
            <SimpleSelect
              label="Franja para llamar"
              value={form.callSlot}
              onChange={set("callSlot")}
              options={CALL_SLOTS}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleCreate} disabled={saving}>
            {saving ? "Creando…" : "Crear lead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
